import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { useToast } from '../components/ui/Toast';
import { formatEUR } from '../lib/format';

type MovementKind = 'INVOICE' | 'CREDIT_NOTE' | 'PAYMENT' | 'REFUND';

interface Movement {
  date: string;
  kind: MovementKind;
  reference: string;
  label: string;
  amount: number;
  balance: number;
  dueDate?: string | null;
  method?: string | null;
}

interface Statement {
  customer: { id: string; name: string; companyName: string | null; billingEmail: string | null };
  period: { from: string; to: string };
  openingBalance: number;
  closingBalance: number;
  movements: Movement[];
  aging: {
    current: number;
    d1_30: number;
    d31_60: number;
    d61_90: number;
    d90_plus: number;
    total: number;
  };
  creditPosition: {
    creditLimit: number | null;
    currentBalance: number;
    reservedCredit: number;
    manualRelease: number;
    available: number | null;
  };
  /** Absent on the B2B portal view — internal bookkeeping health is not the customer's business. */
  reconciliation?: { drift: number; severity: 'OK' | 'WARNING' | 'CRITICAL'; blocked: boolean };
}

const day = (iso: string): string => new Date(iso).toLocaleDateString();
const isoDay = (iso: string): string => iso.slice(0, 10);

/**
 * J7 — statement of account (relevé de compte).
 *
 * One component, two audiences. `/customers/:id/statement` is the ACCOUNTING view; with no `:id`
 * it renders the B2B portal's own statement from `/b2b/account/statement`, where the customer is
 * the JWT's and there is no id to pass.
 *
 * The reconciliation banner is the screen's reason for existing beyond "a list of movements":
 * J7 requires that a statement is never ISSUED for an account whose figures the system itself
 * disputes. The server enforces that on the PDF and email paths; this surfaces WHY, so the
 * blocked button is self-explanatory rather than mysterious.
 */
export default function Statement() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const { id: customerId } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();

  const [data, setData] = useState<Statement | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const qs = [from && `from=${from}`, to && `to=${to}`].filter(Boolean).join('&');
  const base = customerId ? `/customers/${customerId}/statement` : '/b2b/account/statement';

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const { data: s } = await apiClient.get<Statement>(`${base}${qs ? `?${qs}` : ''}`);
      setData(s);
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  }, [base, qs]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void load();
  }, [load]);

  const blocked = data?.reconciliation?.blocked ?? false;

  const openPdf = (): void => {
    // The PDF route streams application/pdf and is bearer-authenticated like every other call,
    // so it is fetched rather than linked, then handed to the viewer as a blob URL.
    setBusy(true);
    apiClient
      .get(`${base}/pdf${qs ? `?${qs}` : ''}`, { responseType: 'blob' })
      .then(({ data: blob }) => {
        const url = URL.createObjectURL(blob as Blob);
        window.open(url, '_blank', 'noopener');
        // Revoked on the next tick — the new tab has already taken its own reference.
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      })
      .catch(showApiError)
      .finally(() => setBusy(false));
  };

  const send = async (): Promise<void> => {
    if (!customerId) return;
    setBusy(true);
    try {
      const { data: res } = await apiClient.post<{ data: { sentTo: string } }>(
        `/customers/${customerId}/statement/send${qs ? `?${qs}` : ''}`,
        {}
      );
      push({ tone: 'success', message: t('statement.sent', { to: res.data.sentTo }) });
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const a = data?.aging;

  return (
    <div>
      <PageHeader
        title={t('statement.title')}
        description={
          data ? data.customer.companyName || data.customer.name : t('statement.description')
        }
        actions={
          <div className="flex flex-wrap items-end gap-2">
            <Input
              type="date"
              aria-label={t('statement.from')}
              value={from}
              onChange={(e) => setParams({ from: e.target.value, ...(to ? { to } : {}) })}
              className="w-40"
            />
            <Input
              type="date"
              aria-label={t('statement.to')}
              value={to}
              onChange={(e) => setParams({ ...(from ? { from } : {}), to: e.target.value })}
              className="w-40"
            />
            <Button
              variant="outline"
              disabled={busy || blocked}
              onClick={openPdf}
              title={blocked ? t('statement.blockedHint') : undefined}
            >
              {t('statement.pdf')}
            </Button>
            {customerId && (
              <Button
                disabled={busy || blocked}
                onClick={() => void send()}
                title={blocked ? t('statement.blockedHint') : undefined}
              >
                {t('statement.send')}
              </Button>
            )}
          </div>
        }
      />

      {data?.reconciliation && data.reconciliation.severity !== 'OK' && (
        <p
          className={`mb-4 rounded border p-3 text-sm ${
            blocked ? 'border-destructive/40 bg-destructive/10' : 'border-warning/40 bg-warning/10'
          }`}
        >
          {blocked
            ? t('statement.blocked', { amount: formatEUR(data.reconciliation.drift) })
            : t('statement.driftWarning', { amount: formatEUR(data.reconciliation.drift) })}
        </p>
      )}

      {data && (
        <>
          <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label={t('statement.openingBalance')} value={formatEUR(data.openingBalance)} />
            <Stat
              label={t('statement.closingBalance')}
              value={formatEUR(data.closingBalance)}
              strong
            />
            <Stat
              label={t('statement.overdue')}
              value={formatEUR(a ? a.total - a.current : 0)}
              tone={a && a.total - a.current > 0 ? 'bad' : undefined}
            />
            <Stat
              label={t('statement.available')}
              value={
                data.creditPosition.available === null
                  ? t('statement.unlimited')
                  : formatEUR(data.creditPosition.available)
              }
            />
          </div>

          <div className="mb-4 overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">{t('statement.col.date')}</th>
                  <th className="px-3 py-2">{t('statement.col.document')}</th>
                  <th className="px-3 py-2">{t('statement.col.reference')}</th>
                  <th className="px-3 py-2 text-right">{t('statement.col.debit')}</th>
                  <th className="px-3 py-2 text-right">{t('statement.col.credit')}</th>
                  <th className="px-3 py-2 text-right">{t('statement.col.balance')}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr className="bg-muted/30 font-medium">
                  <td className="px-3 py-2" colSpan={5}>
                    {t('statement.balanceAt', { date: day(data.period.from) })}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatEUR(data.openingBalance)}
                  </td>
                </tr>
                {data.movements.map((m, i) => (
                  <tr key={`${m.reference}-${i}`} className="hover:bg-muted/20">
                    <td className="px-3 py-2 whitespace-nowrap">{day(m.date)}</td>
                    <td className="px-3 py-2">{t(`statement.kind.${m.kind}`)}</td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {m.reference || '—'}
                      {m.dueDate && (
                        <span className="ml-2 text-muted-foreground">
                          {t('statement.due', { date: day(m.dueDate) })}
                        </span>
                      )}
                      {m.method && <span className="ml-2 text-muted-foreground">{m.method}</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {m.amount > 0 ? formatEUR(m.amount) : ''}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-success">
                      {m.amount < 0 ? formatEUR(-m.amount) : ''}
                    </td>
                    <td className="px-3 py-2 text-right font-medium tabular-nums">
                      {formatEUR(m.balance)}
                    </td>
                  </tr>
                ))}
                {data.movements.length === 0 && !loading && (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                      {t('statement.noMovements')}
                    </td>
                  </tr>
                )}
                <tr className="bg-muted/30 font-semibold">
                  <td className="px-3 py-2" colSpan={5}>
                    {t('statement.balanceAt', { date: day(data.period.to) })}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatEUR(data.closingBalance)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {a && (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">{t('statement.aging')}</th>
                    <th className="px-3 py-2 text-right">{t('statement.bucket.current')}</th>
                    <th className="px-3 py-2 text-right">1–30 j</th>
                    <th className="px-3 py-2 text-right">31–60 j</th>
                    <th className="px-3 py-2 text-right">61–90 j</th>
                    <th className="px-3 py-2 text-right">+90 j</th>
                    <th className="px-3 py-2 text-right">{t('statement.bucket.total')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-3 py-2 font-medium">{t('statement.amounts')}</td>
                    {[a.current, a.d1_30, a.d31_60, a.d61_90, a.d90_plus].map((v, i) => (
                      <td
                        key={i}
                        className={`px-3 py-2 text-right tabular-nums ${
                          i > 0 && v > 0 ? 'font-semibold text-destructive' : ''
                        }`}
                      >
                        {formatEUR(v)}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">
                      {formatEUR(a.total)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          <p className="mt-3 text-xs text-muted-foreground">
            {t('statement.periodNote', {
              from: isoDay(data.period.from),
              to: isoDay(data.period.to),
            })}
          </p>
        </>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  strong,
  tone,
}: {
  label: string;
  value: string;
  strong?: boolean;
  tone?: 'bad';
}) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div
        className={`mt-1 tabular-nums ${strong ? 'text-xl font-bold' : 'text-lg font-semibold'} ${
          tone === 'bad' ? 'text-destructive' : ''
        }`}
      >
        {value}
      </div>
      {tone === 'bad' && (
        <Badge variant="destructive" className="mt-1">
          !
        </Badge>
      )}
    </div>
  );
}
